// ConfirmItsYou.tsx · v12.10.9.4 (#1363 full stop after the reason) · 2026-10-07 · VoiceSpectrum AI
// VoiceSpectrum.Web.T2 · v12.10.9 (Security Phase 1 – #1258/#1259/#1260/#1261) · 2026-10-06
// ConfirmItsYou.tsx - "Confirm it's you" (#1261, S36, UI principles §20). ONE modal for every sensitive action.
//
// How it is used: wrap any call that the server may refuse with 428 {error:'confirm_required'}:
//     await withConfirm(() => securityApi.putPolicy(p), 'You’re changing the sign-in policy for everyone.');
// withConfirm runs the call; on 428 it opens this modal, POSTs /api/auth/confirm (password - always accepted - or a
// text / authenticator code), then RETRIES the original call once. Cancel rejects with ConfirmCancelledError, which
// callers ignore (isConfirmCancelled). Concurrent 428s share one modal.
//
// The modal mounts in its own React root on <body>, so any screen (inside or outside Settings, with or without a provider)
// can call withConfirm. It reads the person's 2-step methods and the confirm window from the /me cache (lib/useAccess).
import { useEffect, useRef, useState } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { ApiError } from '../../lib/api';
import { accountAuthApi, errorBody, isConfirmRequired } from '../../lib/securityApi';
import { getCachedMe } from '../../lib/useAccess';
import { Ic, ScModal, lastFour, plural } from './ui';

export class ConfirmCancelledError extends Error {
  constructor() { super('confirm_cancelled'); this.name = 'ConfirmCancelledError'; }
}
export function isConfirmCancelled(err: unknown): boolean { return err instanceof ConfirmCancelledError; }

type CodeMethod = 'sms' | 'totp';

function confirmErrorText(err: unknown, kind: 'password' | CodeMethod): string {
  const b = errorBody(err);
  if (err instanceof ApiError) {
    if (err.status === 429 || b.error === 'too_many_codes') return `Too many codes. Try again in ${plural(Math.max(1, Math.ceil((b.retryInSeconds ?? 60) / 60)), 'minute')}.`;
    if (err.status === 423 || b.error === 'locked') return 'Too many wrong tries - your account is locked for a while. Try again later.';
    if (b.error === 'bad_code') return `That code isn’t right.${typeof b.triesLeft === 'number' ? ` ${plural(b.triesLeft, 'try', 'tries')} left.` : ''}`;
    if (err.status === 401 || b.error === 'invalid') return kind === 'password' ? 'That password isn’t right.' : 'That code isn’t right.';
  }
  return b.message ?? 'That didn’t work. Try again.';
}

export function ConfirmItsYou({ reason, onDone, onCancel }: { reason?: string; onDone: () => void; onCancel: () => void }) {
  const me = getCachedMe();
  const methods = me?.user.methods ?? null;            // null = unknown -> offer both code kinds
  const hasSms = methods ? methods.includes('sms') : true;
  const hasTotp = methods ? methods.includes('totp') : true;
  const windowMin = me?.policy.confirmWindowMinutes ?? 15;
  const tail = lastFour(me?.user.mobile);

  const [view, setView] = useState<'password' | 'code'>('password');
  const [method, setMethod] = useState<CodeMethod>(hasSms ? 'sms' : 'totp');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [masked, setMasked] = useState<string>(tail ? `•••-•••-${tail}` : '');
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState('');
  const pwRef = useRef<HTMLInputElement>(null);
  const codeRef = useRef<HTMLInputElement>(null);

  useEffect(() => { (view === 'password' ? pwRef.current : codeRef.current)?.focus({ preventScroll: true }); }, [view, method]);

  async function sendText() {
    setSending(true); setErr('');
    try {
      const r = await accountAuthApi.confirmSend();
      if (r?.maskedMobile) setMasked(r.maskedMobile);
    } catch (e) {
      setErr(confirmErrorText(e, 'sms'));
    } finally { setSending(false); }
  }

  function pickCode(m: CodeMethod) {
    setMethod(m); setCode(''); setErr(''); setView('code');
    if (m === 'sms') void sendText();
  }

  const canGo = view === 'password' ? password.length > 0 : code.length === 6;
  async function go() {
    if (!canGo || busy) return;
    setBusy(true); setErr('');
    try {
      if (view === 'password') await accountAuthApi.confirm({ password });
      else await accountAuthApi.confirm({ method, code });
      onDone();
    } catch (e) {
      setErr(confirmErrorText(e, view === 'password' ? 'password' : method));
      if (view === 'code') setCode('');
    } finally { setBusy(false); }
  }

  return (
    <ScModal onClose={onCancel} label="Confirm it’s you" top closeOnScrim={false}>
      <span className="sev info"><Ic k="lock" /></span>
      <h4>Confirm it’s you</h4>
      <p>{reason ? `${/[.!?]$/.test(reason.trim()) ? reason.trim() : reason.trim() + '.'} ` : ''}You won’t be asked again for {plural(windowMin, 'minute')}.</p>   {/* v12.10.9.4 (#1363): the reason always ends with a full stop */}
      {view === 'password' ? (
        <>
          <div className="mrow">
            <label htmlFor="scSuPw">Your password</label>
            <input id="scSuPw" ref={pwRef} type="password" autoComplete="current-password" placeholder="Password" value={password}
              onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void go(); }} />
          </div>
          {(hasSms || hasTotp) && (
            <>
              <div className="sc-or">or</div>
              <div className="sc-btnrow" style={{ marginTop: 0, marginBottom: 14 }}>
                {hasSms && (
                  <button type="button" className="sc-secondary" style={{ flex: '1 1 200px', justifyContent: 'center' }} onClick={() => pickCode('sms')}>
                    <Ic k="sms" /> {tail ? `Text a code to ••• ${tail}` : 'Text me a code'}
                  </button>
                )}
                {hasTotp && (
                  <button type="button" className="sc-secondary" style={{ flex: '1 1 200px', justifyContent: 'center' }} onClick={() => pickCode('totp')}>
                    <Ic k="app" /> Use my authenticator app
                  </button>
                )}
              </div>
            </>
          )}
        </>
      ) : (
        <>
          <div className="mrow">
            <label htmlFor="scSuCode">
              {method === 'sms' ? (masked ? `Code texted to ${masked}` : 'Code texted to your mobile') : 'Code from your authenticator app'}
            </label>
            <input id="scSuCode" ref={codeRef} className="sc-code" inputMode="numeric" maxLength={6} autoComplete="one-time-code" placeholder="••••••" value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} onKeyDown={(e) => { if (e.key === 'Enter') void go(); }} />
          </div>
          <div className="sc-btnrow" style={{ marginTop: -4, marginBottom: 14 }}>
            {method === 'sms' && <button type="button" className="sc-link" disabled={sending} onClick={() => void sendText()}>{sending ? 'Sending…' : 'Send the code again'}</button>}
            {method === 'sms' && hasTotp && <button type="button" className="sc-link" onClick={() => pickCode('totp')}>Use my authenticator app instead</button>}
            {method === 'totp' && hasSms && <button type="button" className="sc-link" onClick={() => pickCode('sms')}>Text me a code instead</button>}
            <button type="button" className="sc-link" onClick={() => { setView('password'); setErr(''); }}>Use my password instead</button>
          </div>
        </>
      )}
      {err && <div className="sc-warn bad" role="alert" style={{ marginBottom: 12 }}><Ic k="warn" /><div>{err}</div></div>}
      <div className="mact">
        <button type="button" className="mc" onClick={onCancel}>Cancel</button>
        <button type="button" className="ma" disabled={!canGo || busy} onClick={() => void go()}>{busy ? 'Checking…' : 'Confirm'}</button>
      </div>
    </ScModal>
  );
}

// ── imperative host: one modal at a time, in its own root ─────────────────────────
let pending: Promise<void> | null = null;
let host: { el: HTMLDivElement; root: Root } | null = null;

function unmount() {
  const h = host; host = null;
  if (h) setTimeout(() => { h.root.unmount(); h.el.remove(); }, 0);
}

/** Open "Confirm it's you". Resolves after a successful POST /api/auth/confirm; rejects with ConfirmCancelledError on cancel. */
export function requestConfirm(reason?: string): Promise<void> {
  if (pending) return pending;
  pending = new Promise<void>((resolve, reject) => {
    const el = document.createElement('div');
    el.setAttribute('data-vs-confirm', '');
    document.body.appendChild(el);
    const root = createRoot(el);
    host = { el, root };
    const finish = (ok: boolean) => { pending = null; unmount(); if (ok) resolve(); else reject(new ConfirmCancelledError()); };
    root.render(<ConfirmItsYou reason={reason} onDone={() => finish(true)} onCancel={() => finish(false)} />);
  });
  return pending;
}

/**
 * Run fn; if the server answers 428 confirm_required, ask "Confirm it's you" and retry fn ONCE.
 * Re-throws every other error (and a second 428) unchanged. Cancel -> ConfirmCancelledError.
 */
export async function withConfirm<T>(fn: () => Promise<T>, reason?: string): Promise<T> {
  try {
    return await fn();
  } catch (err) {
    if (!isConfirmRequired(err)) throw err;
    await requestConfirm(reason);
    return await fn();
  }
}

export default ConfirmItsYou;
